import React, { useEffect } from 'react';
import { Button, Button as MuiButton, FormControl, Grid, InputLabel, Menu, MenuItem, Paper, Select, Typography, SelectChangeEvent, Box } from "@mui/material";
import { LocalizeContextProps, Translate, withLocalize } from 'react-localize-redux';
import { formatDateByLocale } from '../../../utils/index.jsx';
import { FieldWrapper } from '../../../components/general/mini_components';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import dayjs from 'dayjs';

export const TIME_PERIOD = {
    TODAY: 0,
    YESTERDAY: 1,
    LAST_WEEK: 2,
    LAST_MONTH: 3,
    CUSTOM: 4
};

const PERIOD_DAYS_BACK: { [period: number]: number } = {
    [TIME_PERIOD.TODAY]: 0,
    [TIME_PERIOD.YESTERDAY]: 1,
    [TIME_PERIOD.LAST_WEEK]: 7,
    [TIME_PERIOD.LAST_MONTH]: 30
};

// Single source for the range a period covers. The component used to hold two:
// a hardcoded week for the range it opened on, and this switch for the range it
// moved to, so the period it was applying and the period it could name were
// unrelated (SHER-854). Returns null for the custom period, which picks its own
// dates through the two date pickers.
function rangeForPeriod(period: number): Date[] | null {
    const daysBack = PERIOD_DAYS_BACK[period];
    if (daysBack === undefined) {
        return null;
    }
    const start = new Date();
    start.setDate(start.getDate() - daysBack);
    start.setHours(0); start.setMinutes(0);
    return [start, new Date()];
}

interface Props extends LocalizeContextProps{
    onCallBack:(dates:Date[]) => void 
    // The period the selector opens on. A prop rather than a constant so a caller
    // can open on a different window without this component deciding it for every
    // tenant; every caller today wants the same default.
    defaultPeriod?: number
}

function DatesSelector(props: Props) {
    const defaultPeriod = props.defaultPeriod ?? TIME_PERIOD.LAST_MONTH;
    // One answer for "how does this locale write a date", read by the printed range and by
    // both custom-period pickers. Three separate calls used to disagree on whether
    // activeLanguage could be absent; the printed range and the picker fields have to show
    // the same format, so they now read the same value.
    const dateFormat = formatDateByLocale(props.activeLanguage?.code);

    const [initialRange] = React.useState(() => rangeForPeriod(defaultPeriod) ?? [new Date(), new Date()]);
    const [startDate, setStartDate] = React.useState(initialRange[0]);
    const [endDate, setEndDate] = React.useState(initialRange[1]);
    const [startDateCustom, setStartDateCustom] = React.useState(() => {
        const d = new Date();
        d.setHours(0, 0, 0, 0);
        return d;
    });
    const [endDateCustom, setEndDateCustom] = React.useState(new Date());
    
    const [timePeriod, setTimePeriod] = React.useState<number>(defaultPeriod);
    const [openStartDate, setOpenStartDate] = React.useState(false);
    const [openEndDate, setOpenEndDate] = React.useState(false);

    function handleStartDateChange(date: dayjs.Dayjs | null) {
        if (date) {
            setStartDateCustom(date.toDate())
            setOpenStartDate(false);
        }
    };

    function handleEndDateChange(date: dayjs.Dayjs | null) {
        if (date) {
            setEndDateCustom(date.toDate());
            setOpenEndDate(false);
        }
    };

    function selectChanged(event: SelectChangeEvent) {
        const valueInt = parseInt(event.target.value);
        setTimePeriod(valueInt);
        const range = rangeForPeriod(valueInt);
        if (range) {
            setStartDate(range[0]);
            setEndDate(range[1]);
        } else {
            const start = new Date();
            start.setHours(0, 0, 0, 0);
            setStartDateCustom(start);
            setEndDateCustom(new Date());
        }
    }

    useEffect(() => {
        props.onCallBack([startDate, endDate])
    }, [startDate, endDate])

    // SHER-670. The period names ("Last Month", "Semaine dernière") are rolling
    // windows ending now, but the name alone cannot say so: the product owner read
    // a panel labelled "2026-08 - 2026-09" and could not tell 01/08→01/09 from
    // 08/08→08/09. Rather than explain the rule in prose -- a restatement of a value
    // rangeForPeriod() owns, free to drift from it -- print the range that was
    // actually applied. It is read off the same state handed to onCallBack, so the
    // dates on screen are by construction the dates the panels were queried with,
    // including the custom period, where it distinguishes the applied range from the
    // one merely typed into the pickers.
    function renderAppliedRange() {
        return (
            <Typography variant="body2" color="textSecondary">
                <Translate
                    id="hospital.analytics.date_selector.applied_range"
                    data={{
                        start: dayjs(startDate).format(dateFormat),
                        end: dayjs(endDate).format(dateFormat)
                    }}
                />
            </Typography>
        );
    }

    function renderCustomDatesSelector() {
        return (
            <Box 
                sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', md: 'row' },
                    gap: { xs: 2, md: 2 },
                    width: '100%',
                    '& > *': { 
                        width: { xs: '100%', md: 'auto' }
                    }
                }}
            >
                <DesktopDatePicker
                    label="Start Date"
                    defaultValue={dayjs(startDateCustom)}
                    format={dateFormat}
                    maxDate={dayjs()}
                    onChange={handleStartDateChange}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                />
                
                <DesktopDatePicker
                    label="End Date"
                    defaultValue={dayjs(endDateCustom)}
                    format={dateFormat}
                    minDate={dayjs(startDateCustom)}
                    maxDate={dayjs()}
                    onChange={handleEndDateChange}
                    sx={{ width: { xs: '100%', md: 'auto' } }}
                />
                
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        setStartDate(startDateCustom);
                        setEndDate(endDateCustom);
                    }}
                    sx={{
                        width: { xs: '100%', md: 'auto' },
                        lineHeight: '1.25rem',
                        // SHER-562 round 2. The view-mode toggle moved to the right-hand end of
                        // the filter row and is now full height, which leaves the custom period
                        // with seven controls competing for one line. Flex shrinks every child
                        // proportionally, so the button lost enough width to break "Apply
                        // Changes" across two lines. The two date pickers have slack and this
                        // button does not, so hold its width and let them absorb the difference.
                        flexShrink: 0,
                        whiteSpace: 'nowrap'
                    }}
                >
                    Apply Changes
                </Button>
            </Box>
        );
    }
    
    const optionsArray = [
        <MenuItem value={0}><Translate id="hospital.analytics.date_selector.today" /></MenuItem>,
        <MenuItem value={1}><Translate id="hospital.analytics.date_selector.yesterday" /></MenuItem>,
        <MenuItem value={2}><Translate id="hospital.analytics.date_selector.last_week" /></MenuItem>,
        <MenuItem value={3}><Translate id="hospital.analytics.date_selector.last_month" /></MenuItem>,
        <MenuItem value={4}><Translate id="hospital.analytics.date_selector.custom_period" /></MenuItem>
    ];

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box 
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: { xs: 2, md: 2 },
                        width: '100%'
                    }}
                >
                    <FormControl sx={{ width: { xs: '100%', md: '250px' } }}>
                        <InputLabel id="time_period">
                            <Translate id="hospital.analytics.date_selector.select_period"/>
                        </InputLabel>
                        <Select
                            labelId="time_period"
                            id="time_period"
                            label="Select a period"
                            value={timePeriod}
                            onChange={(event) => selectChanged(event)}
                        >
                            {optionsArray}
                        </Select>
                    </FormControl>
                    {timePeriod === TIME_PERIOD.CUSTOM && renderCustomDatesSelector()}

                </Box>
            </Grid>
            <Grid item xs={12}>
                {renderAppliedRange()}
            </Grid>
        </Grid>
    );
}

export default withLocalize(DatesSelector)